import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  Code, Key, Broadcast, Copy, Check, TrashSimple, ArrowsClockwise,
  Plus, X, Warning, CheckCircle, Prohibit, Play, ArrowClockwise,
  Info, Eye, EyeSlash, BookOpen,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Página "Desenvolvedor" — gerenciamento self-service da API pública v1.
 *
 * Duas abas:
 *   1. API Keys     — criar, listar, revogar chaves de API por tenant
 *   2. Webhooks     — inscrever URLs para receber eventos outbound
 *
 * Todas as ações são JWT-authed (owner/admin) — segue a mesma convenção
 * do restante do painel.
 */

const TABS = [
  { id: "keys", label: "API Keys", icon: Key },
  { id: "webhooks", label: "Webhooks", icon: Broadcast },
];

function CopyButton({ value, label = "Copiar" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      toast.success("Copiado!");
      setTimeout(() => setDone(false), 1800);
    } catch {
      toast.error("Falha ao copiar.");
    }
  };
  return (
    <button
      onClick={copy}
      type="button"
      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
      data-testid="dev-copy-btn"
    >
      {done ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      {done ? "Copiado" : label}
    </button>
  );
}

function EnvBadge({ env }) {
  const isLive = env === "live";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
        isLive
          ? "bg-emerald-50 text-emerald-700"
          : "bg-amber-50 text-amber-700"
      }`}
      data-testid={`env-badge-${env}`}
    >
      {isLive ? "LIVE" : "TEST"}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
//  Tab: API Keys
// ─────────────────────────────────────────────────────────────
function ApiKeysTab({ meta }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/dev/api-keys");
      setKeys(data || []);
    } catch (e) {
      toast.error("Falha ao carregar chaves.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm("Revogar esta chave? Aplicações que a usam perderão acesso imediatamente.")) return;
    try {
      await api.post(`/dev/api-keys/${id}/revoke`);
      toast.success("Chave revogada.");
      load();
    } catch (e) {
      toast.error("Falha ao revogar.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir definitivamente esta chave revogada?")) return;
    try {
      await api.delete(`/dev/api-keys/${id}`);
      toast.success("Chave excluída.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    }
  };

  return (
    <div className="space-y-6" data-testid="dev-api-keys-tab">
      {/* Header + ação */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">API Keys</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            O valor da chave aparece <strong>uma única vez</strong> na criação.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 transition-colors"
          data-testid="dev-create-key-btn"
        >
          <Plus size={14} weight="bold" />
          Nova chave
        </button>
      </div>

      {/* Chave recém-criada (mostrada só 1x) */}
      {newKeyValue && (
        <NewKeyBanner
          keyData={newKeyValue}
          onDismiss={() => setNewKeyValue(null)}
        />
      )}

      {/* Lista */}
      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Carregando...</div>
      ) : keys.length === 0 ? (
        <EmptyState
          icon={Key}
          title="Nenhuma chave gerada ainda"
          hint="Crie sua primeira chave para começar a integrar sistemas externos com o AgentUS."
        />
      ) : (
        <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 bg-white overflow-hidden">
          {keys.map((k) => (
            <ApiKeyRow key={k.id} k={k} onRevoke={revoke} onDelete={remove} />
          ))}
        </div>
      )}

      {/* Modal criar */}
      {showCreate && (
        <CreateKeyModal
          meta={meta}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setNewKeyValue(created);
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function ApiKeyRow({ k, onRevoke, onDelete }) {
  const isRevoked = !!k.revoked_at;
  return (
    <div className="p-4 flex items-start justify-between gap-4" data-testid={`api-key-row-${k.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900 truncate">{k.name}</span>
          <EnvBadge env={k.environment} />
          {isRevoked && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              REVOGADA
            </span>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <code className="text-[12px] font-mono text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
            {k.prefix}••••••••
          </code>
          <CopyButton value={k.prefix} label="Copiar prefixo" />
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {k.scopes.map((s) => (
            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
              {s}
            </span>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-slate-400">
          Criada em {new Date(k.created_at).toLocaleDateString("pt-BR")}
          {k.last_used_at && (
            <> · último uso {new Date(k.last_used_at).toLocaleString("pt-BR")}</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isRevoked ? (
          <button
            onClick={() => onRevoke(k.id)}
            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2.5 py-1.5 rounded transition-colors"
            data-testid={`revoke-key-${k.id}`}
          >
            Revogar
          </button>
        ) : (
          <button
            onClick={() => onDelete(k.id)}
            className="text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 px-2.5 py-1.5 rounded transition-colors inline-flex items-center gap-1"
            data-testid={`delete-key-${k.id}`}
          >
            <TrashSimple size={12} />
            Excluir
          </button>
        )}
      </div>
    </div>
  );
}

function NewKeyBanner({ keyData, onDismiss }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5" data-testid="new-key-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-900">Chave criada com sucesso</h3>
          </div>
          <p className="text-[13px] text-slate-600 mt-1.5">
            Copie a chave <strong>agora</strong> — ela não será exibida novamente.
            Se perdê-la, será necessário criar uma nova.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <code
              className="flex-1 text-[13px] font-mono bg-slate-900 text-emerald-300 px-3 py-2 rounded-lg overflow-x-auto"
              data-testid="new-key-value"
            >
              {visible ? keyData.key : "•".repeat(keyData.key.length)}
            </code>
            <button
              onClick={() => setVisible(!visible)}
              className="px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
              title={visible ? "Esconder" : "Mostrar"}
            >
              {visible ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
            <CopyButton value={keyData.key} label="Copiar chave" />
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-600 p-1"
          data-testid="dismiss-new-key"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function CreateKeyModal({ meta, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState("live");
  const [scopes, setScopes] = useState(["customers:read", "customers:write"]);
  const [saving, setSaving] = useState(false);

  const toggleScope = (s) => {
    setScopes(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s]);
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Dê um nome à chave.");
    if (scopes.length === 0) return toast.error("Selecione pelo menos um escopo.");
    try {
      setSaving(true);
      const { data } = await api.post("/dev/api-keys", {
        name: name.trim(), environment, scopes,
      });
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar chave.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-key-modal">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Nova API Key</h3>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Configure permissões e ambiente. Você poderá copiar o valor após criar.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">Nome</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Ex: "Integração Bling"'
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              data-testid="new-key-name-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Rótulo interno — você identifica onde a chave é usada.</p>
          </div>

          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">Ambiente</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {["live", "test"].map((env) => (
                <button
                  key={env}
                  onClick={() => setEnvironment(env)}
                  className={`px-3 py-2 border rounded-lg text-sm transition-all text-left ${
                    environment === env
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`env-btn-${env}`}
                >
                  <div className="font-medium text-slate-900 uppercase text-xs tracking-wider">{env}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {env === "live"
                      ? "Consome créditos. Envia mensagens de verdade."
                      : "Sandbox. Não consome nem envia."}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">Escopos</label>
            <div className="mt-1.5 space-y-1.5">
              {(meta.scopes || []).map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    onChange={() => toggleScope(s)}
                    data-testid={`scope-${s}`}
                    className="rounded border-slate-300"
                  />
                  <code className="text-[12px] font-mono text-slate-700">{s}</code>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors"
            data-testid="submit-create-key"
          >
            {saving ? "Criando..." : "Criar chave"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Tab: Webhooks
// ─────────────────────────────────────────────────────────────
function WebhooksTab({ meta }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/dev/webhooks");
      setSubs(data || []);
    } catch (e) {
      toast.error("Falha ao carregar webhooks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    try {
      await api.post(`/dev/webhooks/${id}/toggle`);
      load();
    } catch (e) {
      toast.error("Falha ao atualizar.");
    }
  };

  const unpause = async (id) => {
    try {
      await api.post(`/dev/webhooks/${id}/unpause`);
      toast.success("Webhook reativado.");
      load();
    } catch (e) {
      toast.error("Falha ao reativar.");
    }
  };

  const test = async (id) => {
    try {
      await api.post(`/dev/webhooks/${id}/test`);
      toast.success("Evento de teste enviado.");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error("Falha ao testar.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta inscrição de webhook?")) return;
    try {
      await api.delete(`/dev/webhooks/${id}`);
      toast.success("Webhook excluído.");
      load();
    } catch (e) {
      toast.error("Falha ao excluir.");
    }
  };

  return (
    <div className="space-y-6" data-testid="dev-webhooks-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Webhooks</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            HMAC-SHA256 · retry automático em ~32h · auto-pausa após 20 falhas.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 transition-colors"
          data-testid="dev-create-webhook-btn"
        >
          <Plus size={14} weight="bold" />
          Nova inscrição
        </button>
      </div>

      {newSecret && (
        <NewSecretBanner
          data={newSecret}
          onDismiss={() => setNewSecret(null)}
        />
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Carregando...</div>
      ) : subs.length === 0 ? (
        <EmptyState
          icon={Broadcast}
          title="Nenhum webhook configurado"
          hint="Crie sua primeira inscrição para começar a receber eventos do AgentUS no seu sistema."
        />
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <WebhookRow
              key={s.id}
              s={s}
              expanded={!!expanded[s.id]}
              onToggleExpanded={() => setExpanded({ ...expanded, [s.id]: !expanded[s.id] })}
              onToggle={toggle}
              onUnpause={unpause}
              onTest={test}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateWebhookModal
          meta={meta}
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setNewSecret(created);
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function WebhookRow({ s, expanded, onToggleExpanded, onToggle, onUnpause, onTest, onDelete }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    api.get(`/dev/webhooks/${s.id}/deliveries`)
      .then(({ data }) => setDeliveries(data || []))
      .catch(() => toast.error("Falha ao carregar entregas."))
      .finally(() => setLoading(false));
  }, [expanded, s.id]);

  const replay = async (did) => {
    try {
      await api.post(`/dev/webhooks/deliveries/${did}/replay`);
      toast.success("Refazendo entrega...");
      setTimeout(async () => {
        const { data } = await api.get(`/dev/webhooks/${s.id}/deliveries`);
        setDeliveries(data || []);
      }, 1500);
    } catch (e) {
      toast.error("Falha no replay.");
    }
  };

  const isPaused = !!s.paused_at;
  const isInactive = !s.active;

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`webhook-row-${s.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[13px] font-mono text-slate-900 truncate">{s.url}</code>
              {isPaused && (
                <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700">
                  PAUSADO
                </span>
              )}
              {isInactive && !isPaused && (
                <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                  INATIVO
                </span>
              )}
              {!isPaused && !isInactive && (
                <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                  ATIVO
                </span>
              )}
            </div>
            {s.description && (
              <p className="text-[12px] text-slate-500 mt-1">{s.description}</p>
            )}
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {s.events.map((e) => (
                <span key={e} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                  {e}
                </span>
              ))}
            </div>
            {s.consecutive_failures > 0 && (
              <p className="text-[11px] text-amber-700 mt-2 inline-flex items-center gap-1">
                <Warning size={12} weight="fill" />
                {s.consecutive_failures} falha{s.consecutive_failures !== 1 ? "s" : ""} consecutiva{s.consecutive_failures !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onTest(s.id)}
              className="p-1.5 rounded hover:bg-slate-50 text-slate-600 hover:text-slate-900"
              title="Enviar evento de teste"
              data-testid={`test-webhook-${s.id}`}
            >
              <Play size={14} />
            </button>
            {isPaused && (
              <button
                onClick={() => onUnpause(s.id)}
                className="text-xs px-2.5 py-1.5 rounded text-red-600 hover:bg-red-50"
                data-testid={`unpause-webhook-${s.id}`}
              >
                Reativar
              </button>
            )}
            <button
              onClick={() => onToggle(s.id)}
              className="text-xs px-2.5 py-1.5 rounded text-slate-600 hover:bg-slate-50"
              data-testid={`toggle-webhook-${s.id}`}
            >
              {s.active ? "Desativar" : "Ativar"}
            </button>
            <button
              onClick={() => onDelete(s.id)}
              className="p-1.5 rounded hover:bg-red-50 text-red-600"
              data-testid={`delete-webhook-${s.id}`}
            >
              <TrashSimple size={14} />
            </button>
          </div>
        </div>
        <button
          onClick={onToggleExpanded}
          className="mt-3 text-[11px] text-slate-500 hover:text-slate-900"
          data-testid={`expand-deliveries-${s.id}`}
        >
          {expanded ? "▼ Ocultar entregas" : "▶ Ver últimas entregas"}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          {loading ? (
            <div className="text-xs text-slate-400 py-4 text-center">Carregando...</div>
          ) : deliveries.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center">
              Nenhuma entrega ainda. Clique em <Play size={11} className="inline" /> Testar para gerar uma.
            </div>
          ) : (
            <div className="space-y-2">
              {deliveries.map((d) => (
                <div key={d.id} className="bg-white rounded-lg border border-slate-200 p-3 text-[12px]">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <DeliveryStatusBadge status={d.status} />
                      <code className="font-mono text-slate-700 truncate">{d.event}</code>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-slate-400 text-[11px]">
                        {new Date(d.created_at).toLocaleString("pt-BR")}
                      </span>
                      <button
                        onClick={() => replay(d.id)}
                        className="p-1 hover:bg-slate-100 rounded text-slate-500"
                        title="Refazer entrega"
                      >
                        <ArrowClockwise size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
                    <span>tentativas: {d.attempts}/{d.max_attempts}</span>
                    {d.response_code && <span>HTTP {d.response_code}</span>}
                    {d.error && <span className="text-red-600">erro: {d.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeliveryStatusBadge({ status }) {
  const map = {
    success: { color: "bg-emerald-100 text-emerald-800", label: "SUCESSO" },
    pending: { color: "bg-slate-100 text-slate-700", label: "PENDENTE" },
    retrying: { color: "bg-amber-100 text-amber-800", label: "REPETINDO" },
    failed: { color: "bg-red-100 text-red-800", label: "FALHOU" },
    exhausted: { color: "bg-red-100 text-red-800", label: "ESGOTADO" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function NewSecretBanner({ data, onDismiss }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5" data-testid="new-secret-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" />
            <h3 className="text-sm font-semibold text-slate-900">Webhook criado</h3>
          </div>
          <p className="text-[13px] text-slate-600 mt-1.5">
            Copie o <strong>secret</strong> abaixo — use-o para validar a assinatura HMAC-SHA256
            de cada request. Este valor <strong>não será mostrado novamente</strong>.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <code className="flex-1 text-[13px] font-mono bg-slate-900 text-emerald-300 px-3 py-2 rounded-lg overflow-x-auto">
              {visible ? data.secret : "•".repeat(data.secret.length)}
            </code>
            <button
              onClick={() => setVisible(!visible)}
              className="px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600"
            >
              {visible ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
            <CopyButton value={data.secret} label="Copiar secret" />
          </div>
        </div>
        <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600 p-1">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function CreateWebhookModal({ meta, onClose, onCreated }) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleEvent = (e) => {
    setEvents(events.includes(e) ? events.filter((x) => x !== e) : [...events, e]);
  };

  const submit = async () => {
    if (!url.trim()) return toast.error("URL é obrigatória.");
    if (events.length === 0) return toast.error("Selecione pelo menos um evento.");
    try {
      setSaving(true);
      const { data } = await api.post("/dev/webhooks", {
        url: url.trim(),
        description: description.trim(),
        events,
      });
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-webhook-modal">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Nova inscrição de webhook</h3>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Selecione os eventos e informe a URL do seu sistema.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">URL de destino</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://seuerp.com/webhook/agentus"
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              data-testid="new-webhook-url-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Precisa ser HTTPS (exceto localhost).</p>
          </div>

          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">Descrição (opcional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Ex: "Sincronia com o Bling"'
              className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            />
          </div>

          <div>
            <label className="text-[12px] font-medium text-slate-700 uppercase tracking-wider">Eventos</label>
            <div className="mt-1.5 space-y-1.5">
              {(meta.events || []).filter((e) => e !== "webhook.test").map((e) => (
                <label key={e} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={events.includes(e)}
                    onChange={() => toggleEvent(e)}
                    data-testid={`event-${e}`}
                    className="rounded border-slate-300"
                  />
                  <code className="text-[12px] font-mono text-slate-700">{e}</code>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
            data-testid="submit-create-webhook"
          >
            {saving ? "Criando..." : "Criar webhook"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-12 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-400 mb-3">
        <Icon size={22} />
      </div>
      <h3 className="text-sm font-medium text-slate-800">{title}</h3>
      <p className="text-[13px] text-slate-500 max-w-sm mx-auto mt-1">{hint}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Página principal
// ─────────────────────────────────────────────────────────────
export default function Developer() {
  const [tab, setTab] = useState("keys");
  const [meta, setMeta] = useState({ scopes: [], events: [] });

  useEffect(() => {
    api.get("/dev/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => toast.error("Falha ao carregar metadados."));
  }, []);

  const docsUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/desenvolvedores";

  return (
    <div className="min-h-screen bg-slate-50">
      <Topbar />
      <div className="max-w-5xl mx-auto px-6 py-8" data-testid="developer-page">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-medium">
            <Code size={13} />
            Desenvolvedor
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">Suas credenciais de integração</h1>
          <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">
            Gere as chaves e configure os webhooks que seu desenvolvedor precisa.
            Toda a documentação técnica está em uma página pública separada.
          </p>
        </header>

        {/* Card: manda o dev pra doc pública */}
        <div
          className="relative overflow-hidden rounded-2xl border border-slate-900/8 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 md:p-6 mb-6"
          data-testid="dev-docs-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-10 h-10 rounded-lg border border-white/10 bg-white/[0.05] flex items-center justify-center shrink-0">
              <BookOpen size={18} className="text-emerald-300" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 font-medium mb-1">
                Documentação para o seu dev
              </div>
              <h3 className="text-[16px] font-semibold tracking-tight text-white">
                Envie este link para o desenvolvedor que vai integrar
              </h3>
              <p className="text-[13px] text-white/60 mt-1 leading-relaxed max-w-lg">
                Ele encontra tudo lá: quickstart, todos os endpoints com exemplos cURL,
                validação HMAC, códigos de erro, FAQ.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <code
                  className="text-[12px] font-mono px-2.5 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-emerald-200"
                  data-testid="dev-docs-url"
                >
                  {docsUrl}
                </code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(docsUrl);
                      toast.success("Link copiado!");
                    } catch { toast.error("Falha ao copiar"); }
                  }}
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-white/70 hover:text-white transition-colors"
                  data-testid="dev-docs-copy"
                >
                  <Copy size={12} />
                  Copiar link
                </button>
                <a
                  href={docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-emerald-300 hover:text-emerald-200 transition-colors"
                  data-testid="dev-docs-open"
                >
                  Abrir portal →
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Card: o que muda depois da integração (checklist rápido) */}
        <details
          className="rounded-xl border border-slate-200 bg-white mb-8 group"
          data-testid="what-changes-card"
        >
          <summary className="cursor-pointer list-none px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Info size={14} className="text-slate-400" />
              <span className="text-[13px] font-medium text-slate-800">
                O que muda no dia-a-dia depois que integramos?
              </span>
            </div>
            <span className="text-[11px] text-slate-400 group-open:hidden">
              ver checklist
            </span>
          </summary>
          <div className="px-5 pb-5 pt-1 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-[13px] text-slate-600">
            <Change label="Cadastros sincronizados" desc="Cliente novo no seu ERP aparece no AgentUS (e vice-versa) sem redigitação." />
            <Change label="Preços sempre atualizados" desc="Alterou no ERP → AgentUS responde o valor certo no WhatsApp em segundos." />
            <Change label="Agendamentos espelhados" desc="Cliente marca no WhatsApp → sua agenda do ERP recebe automaticamente." />
            <Change label="Painel AgentUS continua" desc="WhatsApp, Cérebro, IA e KPIs continuam morando aqui — nada disso migra." />
            <Change label="Auditoria completa" desc="Cada chamada de API fica registrada com IP, chave usada, data e resultado." />
            <Change label="Ambiente de teste" desc="Seu dev testa com chave test sem gastar créditos ou enviar mensagem real." />
          </div>
        </details>

        <nav className="border-b border-slate-200 mb-8">
          <div className="flex gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? "border-slate-900 text-slate-900"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                  data-testid={`dev-tab-${t.id}`}
                >
                  <Icon size={14} weight={active ? "fill" : "regular"} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {tab === "keys" ? (
          <ApiKeysTab meta={meta} />
        ) : (
          <WebhooksTab meta={meta} />
        )}
      </div>
    </div>
  );
}

function Change({ label, desc }) {
  return (
    <div className="flex items-start gap-2.5">
      <Check size={13} weight="bold" className="text-emerald-500 shrink-0 mt-0.5" />
      <div>
        <div className="text-slate-800 font-medium tracking-tight">{label}</div>
        <div className="text-[12px] text-slate-500">{desc}</div>
      </div>
    </div>
  );
}
